import { useEffect, useRef, useState, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { initSocket } from '../services/socketService';
import { useAuthStore } from '../store/authStore';

interface UseSocketOptions {
  conversationId?: string;
  onNewMessage?: (message: any) => void;
  onUserTyping?: (userId: string, isTyping: boolean) => void;
  onMessageRead?: (userId: string, messageId: string) => void;
  onNotification?: (notification: any) => void;
}

export const useSocket = (options: UseSocketOptions = {}) => {
  const { conversationId, onNewMessage, onUserTyping, onMessageRead, onNotification } = options;
  const { isAuthenticated, token } = useAuthStore();
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const typingTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Initialize socket connection
  useEffect(() => {
    // Map is één stabiel object; lokaal vastpakken voor de cleanup.
    const typingTimeouts = typingTimeoutRef.current;
    if (!isAuthenticated || !token) {
      return;
    }

    const socket = initSocket();
    socketRef.current = socket;

    if (!socket) {
      return;
    }

    // Connection status
    socket.on('connect', () => {
      setIsConnected(true);
      console.log('Socket connected');
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      console.log('Socket disconnected');
    });

    // Join conversation room if provided
    if (conversationId) {
      socket.emit('join_conversation', conversationId);
    }

    // New message handler
    if (onNewMessage) {
      socket.on('new_message', (message: any) => {
        // Only handle messages for current conversation
        if (!conversationId || message.conversation_id === conversationId) {
          onNewMessage(message);
        }
      });
    }

    // Typing indicators
    if (onUserTyping) {
      // Server sends { userId, typing: boolean }
      socket.on('user_typing', (data: { userId: string; typing?: boolean; isTyping?: boolean }) => {
        const isTyping = typeof data.isTyping === 'boolean' ? data.isTyping : !!data.typing;
        if (isTyping) {
          setTypingUsers(prev => new Set(prev).add(data.userId));
          
          // Clear typing indicator after 3 seconds
          const existingTimeout = typingTimeoutRef.current.get(data.userId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
          }
          
          const timeout = setTimeout(() => {
            setTypingUsers(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.userId);
              return newSet;
            });
            typingTimeoutRef.current.delete(data.userId);
          }, 3000);
          
          typingTimeoutRef.current.set(data.userId, timeout);
        } else {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          });
          const existingTimeout = typingTimeoutRef.current.get(data.userId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
            typingTimeoutRef.current.delete(data.userId);
          }
        }
        onUserTyping(data.userId, isTyping);
      });
    }

    // Message read handler
    if (onMessageRead) {
      socket.on('message_read', (data: { userId: string; messageId: string }) => {
        onMessageRead(data.userId, data.messageId);
      });
    }

    // Notifications
    if (onNotification) {
      socket.on('new_notification', (notification: any) => {
        onNotification(notification);
      });
    }

    // Error handler
    socket.on('message_error', (error: any) => {
      console.error('Socket message error:', error);
    });

    // Cleanup
    return () => {
      if (conversationId && socket) {
        socket.emit('leave_conversation', conversationId);
      }
      
      // Clear all typing timeouts
      typingTimeouts.forEach(timeout => clearTimeout(timeout));
      typingTimeouts.clear();
      
      // Remove event listeners
      socket.off('connect');
      socket.off('disconnect');
      socket.off('new_message');
      socket.off('user_typing');
      socket.off('message_read');
      socket.off('new_notification');
      socket.off('message_error');
    };
  }, [isAuthenticated, token, conversationId, onNewMessage, onUserTyping, onMessageRead, onNotification]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't disconnect socket completely, just leave conversation
      // Socket should stay connected for other conversations
    };
  }, []);

  const sendMessage = useCallback((conversationId: string, content: string, type: 'text' | 'file' = 'text', fileUrl?: string, fileName?: string) => {
    if (socketRef.current) {
      socketRef.current.emit('send_message', {
        conversationId,
        content,
        type,
        fileUrl,
        fileName
      });
    }
  }, []);

  const startTyping = useCallback((conversationId: string) => {
    if (socketRef.current && conversationId) {
      socketRef.current.emit('typing_start', conversationId);
    }
  }, []);

  const stopTyping = useCallback((conversationId: string) => {
    if (socketRef.current && conversationId) {
      socketRef.current.emit('typing_stop', conversationId);
    }
  }, []);

  const markAsRead = useCallback((conversationId: string, messageId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('mark_read', {
        conversationId,
        messageId
      });
    }
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    typingUsers: Array.from(typingUsers),
    sendMessage,
    startTyping,
    stopTyping,
    markAsRead
  };
};


